import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Upload, Trash2 } from "lucide-react";

type RoomTab = "settings" | "branding";

interface RoomSettingsSectionProps {
  isAdmin: boolean;
  maxEmptyTimeout: number | string;
  setMaxEmptyTimeout: (value: number | string) => void;
  noModeratorTimeout: number | string;
  setNoModeratorTimeout: (value: number | string) => void;
  updatingSettings: boolean;
  hasRoomSettingsChanges: boolean;
  handleSaveSettings: () => void;
  // Branding props
  brandName: string;
  setBrandName: (value: string) => void;
  accentColor: string;
  setAccentColor: (value: string) => void;
  preJoinBgColor: string;
  setPreJoinBgColor: (value: string) => void;
  welcomeMessage: string;
  setWelcomeMessage: (value: string) => void;
  redirectUrl: string;
  setRedirectUrl: (value: string) => void;
  logoUrl: string | null;
  uploadingLogo: boolean;
  handleLogoUpload: (file: File) => void;
  handleLogoRemove: () => void;
}

export function RoomSettingsSection({
  isAdmin,
  maxEmptyTimeout,
  setMaxEmptyTimeout,
  noModeratorTimeout,
  setNoModeratorTimeout,
  updatingSettings,
  hasRoomSettingsChanges,
  handleSaveSettings,
  brandName,
  setBrandName,
  accentColor,
  setAccentColor,
  preJoinBgColor,
  setPreJoinBgColor,
  welcomeMessage,
  setWelcomeMessage,
  redirectUrl,
  setRedirectUrl,
  logoUrl,
  uploadingLogo,
  handleLogoUpload,
  handleLogoRemove,
}: RoomSettingsSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<RoomTab>(
    tabParam === "branding" ? "branding" : "settings",
  );

  const handleTabChange = (tab: RoomTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
          Room Settings
        </h2>
        <p className="text-muted-foreground text-lg">
          Configure your organization's room preferences and branding
        </p>
      </div>

      {/* Horizontal Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-8">
          <button
            onClick={() => handleTabChange("settings")}
            className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors ${
              activeTab === "settings"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => handleTabChange("branding")}
            className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors ${
              activeTab === "branding"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
            }`}
          >
            Branding
          </button>
        </nav>
      </div>

      {isAdmin ? (
        <>
          {/* Tab Content */}
          {activeTab === "settings" ? (
            <div className="space-y-6 animate-fade-in">
              {/* Idle Session Timeout */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="timeout" className="text-lg font-medium">
                    Idle Session Timeout
                  </Label>
                  <p className="text-base text-muted-foreground">
                    Duration in minutes before empty rooms are automatically
                    closed (max 30 mins).
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-32">
                    <Input
                      id="timeout"
                      type="number"
                      min="1"
                      max="30"
                      className="pr-12 text-right h-12 text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={maxEmptyTimeout}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") {
                          setMaxEmptyTimeout("");
                          return;
                        }

                        const numVal = parseInt(val);
                        if (!isNaN(numVal)) {
                          if (numVal > 30) {
                            setMaxEmptyTimeout(30);
                          } else if (numVal < 1) {
                            setMaxEmptyTimeout(1);
                          } else {
                            setMaxEmptyTimeout(numVal);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",
                            "ArrowDown",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      mins
                    </span>
                  </div>
                </div>
              </div>

              {/* No-Moderator Timeout */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="noModTimeout" className="text-lg font-medium">
                    No-Moderator Timeout
                  </Label>
                  <p className="text-base text-muted-foreground">
                    End the room automatically if no moderator is present for
                    this many minutes (max 120 mins).
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-32">
                    <Input
                      id="noModTimeout"
                      type="number"
                      min="1"
                      max="120"
                      className="pr-12 text-right h-12 text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={noModeratorTimeout}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") {
                          setNoModeratorTimeout("");
                          return;
                        }
                        const numVal = parseInt(val);
                        if (!isNaN(numVal)) {
                          if (numVal > 120) {
                            setNoModeratorTimeout(120);
                          } else if (numVal < 1) {
                            setNoModeratorTimeout(1);
                          } else {
                            setNoModeratorTimeout(numVal);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",
                            "ArrowDown",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      mins
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSettings}
                  disabled={updatingSettings || !hasRoomSettingsChanges}
                  size="lg"
                  className={`h-12 px-8 text-base transition-all ${
                    hasRoomSettingsChanges
                      ? "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
                      : "opacity-50"
                  }`}
                >
                  {updatingSettings ? "Saving Changes..." : "Save Changes"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in">
              {/* Logo Upload */}
              <div className="p-6 border border-border rounded-lg space-y-4">
                <Label className="text-lg font-medium">Logo</Label>
                <p className="text-sm text-muted-foreground">
                  Upload your brand logo (PNG, JPEG, or SVG, max 2MB)
                </p>
                <div className="flex items-center gap-4">
                  {logoUrl ? (
                    <div className="relative w-16 h-16 rounded-lg border border-border overflow-hidden bg-muted/30 flex items-center justify-center">
                      <img
                        src={logoUrl}
                        alt="Brand logo"
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-dashed border-border flex items-center justify-center bg-muted/20">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleLogoUpload(file);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo
                        ? "Uploading..."
                        : logoUrl
                          ? "Replace"
                          : "Upload"}
                    </Button>
                    {logoUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLogoRemove}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Brand Name */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="brandName" className="text-lg font-medium">
                    Brand Name
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Displayed in the room header instead of "FLOWLABS"
                  </p>
                </div>
                <Input
                  id="brandName"
                  className="w-64 h-12 text-base"
                  placeholder="Your Brand"
                  maxLength={50}
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                />
              </div>

              {/* Accent Color */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="accentColor" className="text-lg font-medium">
                    Accent Color
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Primary color for buttons and interactive elements
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={accentColor || "#ff7a1a"}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="w-10 h-10 rounded border border-border cursor-pointer"
                  />
                  <Input
                    id="accentColor"
                    className="w-32 h-12 text-base font-mono"
                    placeholder="#ff7a1a"
                    maxLength={7}
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                  />
                </div>
              </div>

              {/* Pre-Join Background Color */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label
                    htmlFor="preJoinBgColor"
                    className="text-lg font-medium"
                  >
                    Pre-Join Background
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Background color on the join screen
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={preJoinBgColor || "#141418"}
                    onChange={(e) => setPreJoinBgColor(e.target.value)}
                    className="w-10 h-10 rounded border border-border cursor-pointer"
                  />
                  <Input
                    id="preJoinBgColor"
                    className="w-32 h-12 text-base font-mono"
                    placeholder="#141418"
                    maxLength={7}
                    value={preJoinBgColor}
                    onChange={(e) => setPreJoinBgColor(e.target.value)}
                  />
                </div>
              </div>

              {/* Welcome Message */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label
                    htmlFor="welcomeMessage"
                    className="text-lg font-medium"
                  >
                    Welcome Message
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Displayed on the pre-join screen
                  </p>
                </div>
                <Input
                  id="welcomeMessage"
                  className="w-80 h-12 text-base"
                  placeholder="Welcome to our session!"
                  maxLength={200}
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                />
              </div>

              {/* Redirect URL */}
              <div className="flex items-center justify-between p-6 border border-border rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="redirectUrl" className="text-lg font-medium">
                    Post-Session Redirect
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Redirect participants here after the session ends
                  </p>
                </div>
                <Input
                  id="redirectUrl"
                  className="w-80 h-12 text-base"
                  placeholder="https://yoursite.com"
                  maxLength={500}
                  value={redirectUrl}
                  onChange={(e) => setRedirectUrl(e.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSettings}
                  disabled={updatingSettings || !hasRoomSettingsChanges}
                  size="lg"
                  className={`h-12 px-8 text-base transition-all ${
                    hasRoomSettingsChanges
                      ? "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
                      : "opacity-50"
                  }`}
                >
                  {updatingSettings ? "Saving Changes..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="p-6 bg-muted/30 border-border/50">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium text-lg">Admin Access Required</p>
              <p className="text-base text-muted-foreground">
                Only organization admins can modify room settings.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
